import React, { useState, useEffect, useMemo } from "react";
import { Autocomplete, TextField, CircularProgress, Checkbox, Typography, Stack, InputLabel } from "@mui/material";
import { debounce } from "lodash";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError";
import { GlobalState } from "context/GlobalContext";
import { constraintsOptions, inputTypes } from "data/globalData";
import { grey } from "@mui/material/colors";

const SELECT_ALL = { key: "__select_all__", name: "Select All" };

const SearchInputFieldWithDebounce = ({
	column,
	data,
	setData,
	fetchData,
	selectOptKeyName,
	disableLabel = false,
	width = "100%",
}) => {
	const { setSnackBar, setSnackBarOpen, snackBar, setDropdownDataCache, getDropdownCache } = GlobalState();

	const required = column?.constraints[constraintsOptions.NOTNULL.key] ? true : false;

	const [query, setQuery] = useState("");
	const [loading, setLoading] = useState(false);
	const [optionsList, setOptionsList] = useState([]);

	const multiple = column.input_type === inputTypes.MULTIPLE_SELECT.key;
	const selectedKeys = data?.[column.key] ?? (multiple ? [] : "");

	// Normalize result (from static or API)
	const normalize = (list = []) => {
		return list.map((opt) => {
			if (typeof column[selectOptKeyName]?.getOptionLabel === "function")
				return { ...opt, ...column[selectOptKeyName]?.getOptionLabel(opt) };
			else return opt;
		});
	};
	const removeDuplicates = (list = []) => {
		return Object.values(
			list.reduce((acc, row) => {
				if (row.key !== SELECT_ALL.key) acc[row.key] = row;
				return acc;
			}, {})
		);
	};

	const fetchDefaultData = async () => {
		const cacheKey = column.key;
		const cached = getDropdownCache(cacheKey);
		if (cached) {
			setOptionsList(multiple ? [SELECT_ALL, ...cached] : cached);
			return;
		}
		setLoading(true);
		try {
			const res = await fetchData();
			const result = res?.data?.result ?? [];
			const normalized = normalize(result);
			setOptionsList(multiple ? [SELECT_ALL, ...normalized] : normalized);
			setDropdownDataCache(cacheKey, normalized);
		} catch (err) {
			setSnackBar({
				...snackBar,
				message: parseErrMsgFromError(err),
				severity: "error",
			});
			setSnackBarOpen(true);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchDefaultData();
	}, []);

	const fetchSuggestions = async (searchTerm) => {
		if (!searchTerm.trim()) return;
		setLoading(true);
		try {
			const res = await fetchData(0, 20, searchTerm);
			const result = res?.data?.result ?? [];
			const normalized = normalize(result);
			setOptionsList((prev) =>
				multiple
					? [SELECT_ALL, ...removeDuplicates([...normalized, ...prev.filter((row) => row.key !== SELECT_ALL.key)])]
					: removeDuplicates([...normalized, ...prev.filter((row) => row.key !== SELECT_ALL.key)])
			);
		} catch (error) {
			console.error("Error fetching suggestions:", error);
		} finally {
			setLoading(false);
		}
	};

	const debouncedFetch = useMemo(() => debounce(fetchSuggestions, 800), []);
	useEffect(() => {
		if (!query) {
			// setOptionsList(multiple ? [SELECT_ALL, ...defaultLoad] : defaultLoad);
		} else {
			debouncedFetch(query);
		}
	}, [query, debouncedFetch]);

	useEffect(() => {
		return () => debouncedFetch.cancel();
	}, [debouncedFetch]);

	const isAllSelected = multiple && selectedKeys.length === optionsList.length && optionsList.length > 0;
	const hasValues = Array.isArray(data[column.key]) && data[column.key].length > 0;
	// console.log(query, "nbsadjsa");
	const handleChange = (event, newValue) => {
		if (!multiple) {
			setData((prev) => ({
				...prev,
				[column.key]: newValue?.key ?? "",
			}));
			return;
		}

		const lastSelected = newValue[newValue.length - 1];
		if (lastSelected?.key === SELECT_ALL.key) {
			setData((prev) => ({
				...prev,
				[column.key]: isAllSelected ? [] : optionsList.map((opt) => opt.key),
			}));
			return;
		}

		const filtered = newValue.filter((opt) => opt.key !== SELECT_ALL.key);
		setData((prev) => ({
			...prev,
			[column.key]: filtered.map((opt) => opt.key),
		}));
	};

	const value = useMemo(() => {
		if (!multiple) {
			return optionsList.find((opt) => opt.key === selectedKeys) ?? null;
		}
		return optionsList.filter((opt) => opt.key !== SELECT_ALL.key && selectedKeys.includes(opt.key));
	}, [selectedKeys, optionsList, multiple]);

	return (
		<Stack sx={{ width }} spacing={"3px"} height={disableLabel ? "40px" : "70px"}>
			{!disableLabel && (
				<InputLabel sx={{ overflow: "hidden", color: grey[600] }}>
					{column?.name}
					{required && <span style={{ color: "red" }}>*</span>}
				</InputLabel>
			)}
			<Autocomplete
				multiple={multiple}
				disableCloseOnSelect={multiple}
				// freeSolo={false}
				options={optionsList}
				loading={loading}
				value={value}
				inputValue={query}
				onChange={handleChange}
				onInputChange={(event, newInputValue, reason) => {
					if (reason === "input") {
						setQuery(newInputValue);
					}
				}}
				getOptionLabel={(option) => option.name}
				isOptionEqualToValue={(opt, val) => opt.key === val.key}
				renderTags={(value) => (
					<span
						style={{
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}>
						{value.map((opt) => opt.name).join(", ")}
					</span>
				)}
				renderOption={(props, option, { selected }) => {
					const isSelectAll = option.key === SELECT_ALL.key;
					const isIndeterminate = isSelectAll && selectedKeys.length > 0 && selectedKeys.length < optionsList.length;
					const isChecked = isSelectAll ? isAllSelected : selected;

					return (
						<li {...props} key={option.key}>
							{multiple && (
								<Checkbox
									checked={isChecked}
									indeterminate={isIndeterminate}
									sx={{ margin: 0, padding: "2px", mr: 1 }}
									disableRipple
								/>
							)}
							<Typography color={isSelectAll ? "green" : "inherit"}>{option.name}</Typography>
						</li>
					);
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						label={`Select ${column.name}`}
						variant="outlined"
						size="small"
						fullWidth
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{loading && <CircularProgress size={20} />}
									{params.InputProps.endAdornment}
								</>
							),
						}}
					/>
				)}
				sx={{
					"& .MuiAutocomplete-inputRoot": {
						display: "flex",
						alignItems: "center",
						flexWrap: "nowrap",
						height: "40px",
						overflow: "hidden",
						width: "100%", // Ensure input takes the remaining width
					},
					"& .MuiAutocomplete-tag": {
						flex: hasValues ? "1 1 100%" : "0 0 0%", // Tag should take 60% width when there are selected values
						width: hasValues ? "100%" : "0%",
						overflow: "hidden",
						whiteSpace: "nowrap",
						textOverflow: "ellipsis",
						marginRight: "4px",
						transition: "all 0.3s ease",
						display: hasValues ? "flex" : "none", // Only show tags when there are values
					},
					"& .MuiAutocomplete-input": {
						flex: hasValues ? "0 0 0%" : "1 1 100%", // When tags are present, hide input; else take full width
						minWidth: 0,
						width: hasValues ? "1px" : "100%",
						pointerEvents: hasValues ? "none" : "auto",
						transition: "all 0.3s ease",
						display: hasValues ? "none" : "block", // Only show tags when there are values
						padding: 0,
					},
					// "& .MuiAutocomplete-clearIndicator": {
					// 	display: "none",
					// },
					// On hover or focus — reveal input and resize tags
					"&.Mui-focused .MuiAutocomplete-input, &:hover .MuiAutocomplete-input": hasValues
						? {
								flex: "0 0 40%",
								width: "auto",
								pointerEvents: "auto",
								display: "block",
						  }
						: {},
					"&.Mui-focused .MuiAutocomplete-tag, &:hover .MuiAutocomplete-tag": hasValues
						? {
								flex: "0 0 60%",
								maxWidth: "60%",
								display: "flex",
						  }
						: {},
				}}
			/>
		</Stack>
	);
};

export default SearchInputFieldWithDebounce;
